/**
 * Customer-order invoice flow helpers.
 *
 * The API exposes `invoice_flow` on each customer order scope (suborder or
 * standalone order) describing who produces the customer invoice and how:
 * - "connected_vendor": the vendor uses Safetee and produces the invoice
 *   there (generated or uploaded); the agent may upload it on their behalf
 * - "offline_vendor": vendor not connected, the agent uploads the PDF
 *   (possibly received by email in reply to the order form)
 * - "agent_sale_upload": agent direct sale, the agent uploads an invoice file
 * - "agent_sale_draft": agent direct sale, the agent validates the
 *   auto-created draft invoice
 */

export const INVOICE_FLOW = Object.freeze({
    CONNECTED_VENDOR: "connected_vendor",
    OFFLINE_VENDOR: "offline_vendor",
    AGENT_SALE_UPLOAD: "agent_sale_upload",
    AGENT_SALE_DRAFT: "agent_sale_draft",
});

/**
 * Pending invoice action kinds, from the agent's point of view.
 * - "upload": the agent must upload an invoice PDF
 * - "draft": the agent must validate an auto-created draft invoice
 * - "vendor": the connected vendor produces the invoice on Safetee
 *   (the agent may still upload it on their behalf)
 * - "unknown": flow information unavailable (legacy payload)
 * @typedef {"upload" | "draft" | "vendor" | "unknown"} InvoicePendingKind
 */

const AGENT_UPLOAD_FLOWS = [
    INVOICE_FLOW.OFFLINE_VENDOR,
    INVOICE_FLOW.AGENT_SALE_UPLOAD,
];

const AGENT_DIRECT_SALE_FLOWS = [
    INVOICE_FLOW.AGENT_SALE_UPLOAD,
    INVOICE_FLOW.AGENT_SALE_DRAFT,
];

const VENDOR_INVOICE_FLOWS = [
    INVOICE_FLOW.CONNECTED_VENDOR,
    INVOICE_FLOW.OFFLINE_VENDOR,
];

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {boolean}
 */
function orderHasParent(order) {
    return order?.parent != null && order.parent !== "";
}

/**
 * True when the order is a brokerage master (needs or already has sub-orders).
 * @param {Record<string, unknown> | null | undefined} order
 * @param {Array<Record<string, unknown>> | null | undefined} [suborders]
 * @returns {boolean}
 */
function orderHasOrNeedsSuborders(order, suborders) {
    if (!order) {
        return false;
    }
    if (order.suborders_needed) {
        return true;
    }
    if (Number(order.suborders_count) > 0) {
        return true;
    }
    return (suborders || []).length > 0;
}

/**
 * True when the scope is an agent direct sale (own catalog, no vendor).
 * Falls back to matching the master's seller entity (agent-mirror suborder)
 * when `invoice_flow` is missing from the payload.
 * @param {Record<string, unknown> | null | undefined} scopeOrder
 * @param {Record<string, unknown> | null | undefined} [masterOrder]
 * @returns {boolean}
 */
export function isDirectSaleSuborder(scopeOrder, masterOrder) {
    if (!scopeOrder || scopeOrder.status === "canceled") {
        return false;
    }
    if (AGENT_DIRECT_SALE_FLOWS.includes(scopeOrder.invoice_flow)) {
        return true;
    }
    if (VENDOR_INVOICE_FLOWS.includes(scopeOrder.invoice_flow)) {
        return false;
    }
    const masterSeller = masterOrder?.seller_entity;
    if (masterSeller == null || scopeOrder.seller_entity == null) {
        return false;
    }
    return scopeOrder.seller_entity == masterSeller;
}

/**
 * Missing-margin (force purchase price) only applies to direct sale:
 * a standalone order (no sub-orders), or at least one non-canceled
 * direct-sale sub-order. Brokerage masters with only vendor sub-orders
 * are not direct sale — even before sub-orders are created.
 * @param {Record<string, unknown> | null | undefined} order
 * @param {Array<Record<string, unknown>> | null | undefined} [suborders]
 * @returns {boolean}
 */
export function orderHasDirectSaleScope(order, suborders) {
    if (!order) {
        return false;
    }
    if (orderHasParent(order)) {
        return isDirectSaleSuborder(order, order.parent_data || null);
    }
    const loadedSuborders = suborders || [];
    if (!orderHasOrNeedsSuborders(order, loadedSuborders)) {
        return true;
    }
    return loadedSuborders.some((suborder) =>
        isDirectSaleSuborder(suborder, order)
    );
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @param {Array<Record<string, unknown>> | null | undefined} [suborders]
 * @returns {boolean}
 */
export function shouldShowMissingRevenueWarning(order, suborders) {
    if (!order || !order.missing_revenue || order.status === "canceled") {
        return false;
    }
    return true;
}

/**
 * Invoices attached to a customer-order scope (`invoice.order`).
 * @param {Array<Record<string, unknown>> | null | undefined} invoices
 * @param {Record<string, unknown> | null | undefined} scopeOrder
 * @returns {Array<Record<string, unknown>>}
 */
export function invoicesForScope(invoices, scopeOrder) {
    if (!scopeOrder || !invoices || !invoices.length) {
        return [];
    }
    const scopeId = String(scopeOrder.id);
    return invoices.filter((invoice) => String(invoice.order) === scopeId);
}

/**
 * @param {Array<Record<string, unknown>> | null | undefined} scopeInvoices
 * @returns {boolean}
 */
function scopeHasDraftInvoice(scopeInvoices) {
    return (scopeInvoices || []).some((invoice) => invoice?.status === "draft");
}

/**
 * Validated (or otherwise non-draft) invoice already on the scope.
 * Sending it is a separate action from "missing invoice / waiting vendor".
 * @param {Array<Record<string, unknown>> | null | undefined} scopeInvoices
 * @returns {boolean}
 */
function scopeHasConcreteInvoice(scopeInvoices) {
    return (scopeInvoices || []).some(
        (invoice) => invoice?.status && invoice.status !== "draft"
    );
}

/**
 * Classify the pending invoice action on an order scope.
 * @param {Record<string, unknown> | null | undefined} scopeOrder
 * @param {Array<Record<string, unknown>> | null | undefined} [scopeInvoices]
 * @returns {InvoicePendingKind | null} null when nothing is pending
 */
export function invoicePendingKind(scopeOrder, scopeInvoices) {
    if (!scopeOrder || scopeOrder.status === "canceled") {
        return null;
    }
    if (scopeOrder.invoices_ready) {
        return null;
    }
    // Prefer an actionable draft over the flow-based pending kind (matches
    // the API annotation: draft_invoices_count > 0 wins over upload/vendor).
    if (
        Number(scopeOrder.draft_invoices_count) > 0 ||
        scopeHasDraftInvoice(scopeInvoices)
    ) {
        return "draft";
    }
    // A validated invoice is already there: remaining work is send/pay, not
    // "upload / wait for vendor". `invoices_ready` can stay false (e.g.
    // connected-vendor upload with biller unset).
    if (scopeHasConcreteInvoice(scopeInvoices)) {
        return null;
    }
    const flow = scopeOrder.invoice_flow;
    if (AGENT_UPLOAD_FLOWS.includes(flow)) {
        return "upload";
    }
    if (flow === INVOICE_FLOW.AGENT_SALE_DRAFT) {
        return "draft";
    }
    if (flow === INVOICE_FLOW.CONNECTED_VENDOR) {
        return "vendor";
    }
    return "unknown";
}

/**
 * Pending-invoice chip for order lists: i18n key (under
 * ``orderinvoices.status.``) and chip color, from ``invoice_pending_kind``
 * (annotated by the API) with a legacy invoice-count fallback.
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {{ key: string, color: string } | null} null when invoices are ready
 */
export function orderInvoicePendingChip(order) {
    if (!order || order.invoices_ready) {
        return null;
    }
    switch (order.invoice_pending_kind) {
        case "upload":
            return { key: "to_upload", color: "error" };
        case "draft":
            return { key: "draft_to_validate", color: "warning" };
        case "vendor":
            return { key: "waiting_vendor", color: "info" };
    }
    if (!order.invoices_count) {
        return { key: "missing_invoices", color: "error" };
    }
    return { key: "waiting_validation", color: "warning" };
}

/**
 * @typedef {Object} InvoiceFlowCounts
 * @property {number} upload
 * @property {number} draft
 * @property {number} vendor
 * @property {number} unknown
 * @property {number} pending total of the four kinds above
 */

/**
 * Count pending invoice actions across suborders.
 * @param {Array<Record<string, unknown>> | null | undefined} suborders
 * @param {Array<Record<string, unknown>> | null | undefined} [invoices]
 * @returns {InvoiceFlowCounts}
 */
export function summarizeInvoiceFlows(suborders, invoices) {
    const counts = { upload: 0, draft: 0, vendor: 0, unknown: 0, pending: 0 };
    (suborders || []).forEach((suborder) => {
        const kind = invoicePendingKind(
            suborder,
            invoicesForScope(invoices, suborder)
        );
        if (!kind) {
            return;
        }
        counts[kind] += 1;
        counts.pending += 1;
    });
    return counts;
}

/**
 * @typedef {Object} BillingModePendingCounts
 * @property {number} draft
 * @property {number} unsent
 * @property {number} upload
 * @property {number} vendor
 * @property {number} unknown
 * @property {number} missing upload + vendor + unknown
 * @property {number} total draft + unsent + missing
 */

/**
 * Merge concrete invoice counters with flow-based pending scopes for the
 * order-view billing badge, without double-counting a draft or unsent
 * invoice that still looks like upload/vendor pending because
 * ``invoices_ready`` is false.
 *
 * @param {Object} params
 * @param {number} [params.draftInvoicesCount]
 * @param {number} [params.unsentInvoicesCount]
 * @param {Array<Record<string, unknown>> | null | undefined} [params.scopes]
 * @param {Array<Record<string, unknown>> | null | undefined} [params.invoices]
 * @returns {BillingModePendingCounts}
 */
export function billingModePendingCounts({
    draftInvoicesCount = 0,
    unsentInvoicesCount = 0,
    scopes = [],
    invoices = [],
} = {}) {
    const draftCount = Number(draftInvoicesCount) || 0;
    const unsentCount = Number(unsentInvoicesCount) || 0;
    const flowCounts = summarizeInvoiceFlows(scopes, invoices);

    // Concrete drafts cover scopes that would otherwise still appear as
    // upload/vendor/unknown while invoices_ready is false.
    const draftsOnMissingFlows = Math.max(0, draftCount - flowCounts.draft);
    const upload = flowCounts.upload;
    const vendor = flowCounts.vendor;
    const unknown = flowCounts.unknown;
    const rawMissing = upload + vendor + unknown;
    const missing = Math.max(0, rawMissing - draftsOnMissingFlows);

    // Drop missing kinds proportionally from the tooltip breakdown so the
    // sum of parts matches ``missing`` (prefer keeping upload, then vendor).
    let remainingDeduction = draftsOnMissingFlows;
    const deduct = (value) => {
        const used = Math.min(value, remainingDeduction);
        remainingDeduction -= used;
        return value - used;
    };
    const uploadAdjusted = deduct(upload);
    const vendorAdjusted = deduct(vendor);
    const unknownAdjusted = deduct(unknown);

    const draft = Math.max(draftCount, flowCounts.draft);
    return {
        draft,
        unsent: unsentCount,
        upload: uploadAdjusted,
        vendor: vendorAdjusted,
        unknown: unknownAdjusted,
        missing,
        total: draft + unsentCount + missing,
    };
}
